import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../common/context/';
import {
    InterruptionsAPI,
    PreliquidationDaySummariesBonusRulesAPI,
    requests,
    WorkshiftsSchedulingAPI,
    WorkshiftsSchedulingBusinessUnitAPI,
    WorkshiftsSchedulingCostCenterAPI,
} from '../../../services/external/APIs';
import ContextMenu from '../controller-modals/context-menu/ContextMenu';
import { Props } from './EmployeesTableProps';
import { Table } from './Table';

import { LocalStorage_keys } from '../../../constants';
import {
    AdditionalHourModel,
    EmployeeNodeModel,
    InterruptionModel,
    NewAdditionalTimeModel,
    NewInterruptionModel,
    SchedulingWorkshiftModel,
    UpdatedInterruption,
} from '../../../models';
import { AssingAvailabilityAPI } from '../../../services/external/APIs/assing-availability/AssingAvailabilityAPI';
import { TimeAttendanceEventsAPI } from '../../../services/external/APIs/timeAttendanceEvents/TimeAttendanceEventsAPI';
import { covertFormatHour, formatDate, getEmployeesIds } from '../../../utils';
import { AssociateTimeAttendanceEvents } from '../modals/time-attendance-events/associate-time-attendance-events/AssociateTimeAttendanceEvents';
import { UpdateTimeAttendanceEvents } from '../modals/time-attendance-events/modify-time-attendance-events/UpdateTimeAttendanceEvents';
import { ModalsWrapper } from './ModalsWrapper';
import { OperationsCenterRepository } from '../../../services/external/APIs/operations-center/OperationsCenter.api';
import { MessageModal } from '../custom-toast/MessageModal';

/**
 * Este componente contiene la tabla de la grilla de las programaciones, de igual manera contiene toda la logica para mostrar
 * los diferentes modales dependiendo del evento actual, de igual manera las funciones que ocurriran en cada modal
 * @param Props
 * @returns Componente que contiene todos los modales y componentes principales de la vista de la grilla y los eventos de las acciones
 */
export const EmployeesTable = ({
    selectedEmployees,
    selectedEmployeesCopy,
    startDate,
    endDate,
    groupList,
    isCollapseTree,
    consultInfoScheduling,
}: Props) => {
    const {
        grid,
        langResources,
        modalInfoWorkshift,
        setGrid,
        handlerBusinessUnitsModal,
        handlerOpenAdditionalHours,
        handlerOpenAidTypeModal,
        handlerOpenCostCenterModal,
        handlerOpenInteruptionModal,
        handlerOpenDeleteWorkshiftModal,
        handlerOpenUpdateAndDeleteAdditionalHoursModal,
        handlerOpenUpdateAndDeleteInterruptionsModal,
        handlerOpenWorkshiftModal,
        handlerOpenBonusModal,
        handlerDisassociatedBusinessUnitsModal,
        handlerOpenDisassociatedCostCenterModal,
        handlerCopyWorkshiftsSchedulings,
        handlerSavePasteWorkshiftsSchedulings,
        setIsOpenDeleteWorkshiftModal,
        setIsOpenBusinessUnitsModal,
        setIsOpenWorkshiftModal,
        setIsOpenUpdateAndDeleteAdditionalHoursModal,
        setIsOpenUpdateAndDeleteModal,
        setIsOpenCostCenterModal,
        saveExceptionalWorkshiftsScheduling,
        handlerOpenModifyMarkingModal,
        handlerOpenAssociateMarkingModal,
        handlerValidateNovelties,
        handlerOpenMultipleCostCentersByHourRangesModal,
        setIsOpenDetailsInconsistenciesModal,
        inconsistenciesTypesToFilter,
        setIsOpenCostCenterScheduleResultsModal,
        setCostCenterScheduleResults,
    } = useContext(AppContext);

    const [showMessageModal, setShowMessageModal] = useState(false);
    const [message, setMessage] = useState('');

    /**
     * Creamos los centros de costo
     * @param initDate string
     * @param lastDate string
     * @param employee EmployeeNodeModel[]
     * @param costCenterId number
     */
    const saveCostCenter = (
        initDate: string,
        lastDate: string,
        employee: EmployeeNodeModel[],
        costCenterId: number
    ) => {
        if (costCenterId) {
            let recoveredData = window.localStorage.getItem(
                LocalStorage_keys.SELECTION_RANGE
            );

            if (recoveredData) {
                let data: Array<{ employeeId: string | number; date: string }> =
                    JSON.parse(recoveredData);

                data.forEach((item) => {
                    let body = {
                        startDate: item.date + 'T00:00',
                        endDate: item.date + 'T00:00',
                        costCenterId: costCenterId,
                        employeeIds: [item.employeeId],
                    };

                    WorkshiftsSchedulingCostCenterAPI.saveWorkshiftCostCenter(body)
                        .then((res) => {
                            consultInfoScheduling(
                                selectedEmployees,
                                startDate,
                                endDate,
                                false,
                                []
                            );
                        })
                        .catch((err) => {
                            toast.error(langResources['save.cost.center']['error'], {
                                type: 'error',
                                isLoading: false,
                                autoClose: 5000,
                                position: toast.POSITION.BOTTOM_RIGHT,
                                closeOnClick: true,
                                closeButton: true,
                            });
                        });
                });
                setIsOpenCostCenterModal(false);
                window.localStorage.removeItem(LocalStorage_keys.SELECTION_RANGE);
            } else {
                const id = toast.loading(
                    langResources['save.cost.center']['loading']
                );

                setIsOpenCostCenterModal(false);

                let body = {
                    startDate: initDate + 'T' + '00:00',
                    endDate: lastDate + 'T' + '00:00',
                    costCenterId: costCenterId,
                    employeeIds: getEmployeesIds(employee),
                };

                WorkshiftsSchedulingCostCenterAPI.saveWorkshiftCostCenter(body)
                    .then((res) => {
                        toast.update(id, {
                            render: langResources['save.cost.center']['success'],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.cost.center']['error'],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            }
        }
    };

    /**
     * Creamos las unidades de negocio
     * @param initDate string
     * @param lastDate string
     * @param employee EmployeeNodeModel[]
     * @param businessUnitId number
     */
    const saveBusinessUnits = (
        initDate: string,
        lastDate: string,
        employee: EmployeeNodeModel[],
        businessUnitId: number
    ) => {
        if (businessUnitId) {
            let recoveredData = window.localStorage.getItem(
                LocalStorage_keys.SELECTION_RANGE
            );

            if (recoveredData) {
                let data: Array<{ employeeId: string | number; date: string }> =
                    JSON.parse(recoveredData);

                data.forEach((item) => {
                    let body = {
                        startDate: item.date + 'T00:00',
                        endDate: item.date + 'T00:00',
                        businessUnitId,
                        employeeIds: [item.employeeId],
                    };

                    WorkshiftsSchedulingBusinessUnitAPI.saveWorkshiftBusinessUnit(
                        body
                    )
                        .then((res) => {
                            consultInfoScheduling(
                                selectedEmployees,
                                startDate,
                                endDate,
                                false,
                                []
                            );
                        })
                        .catch((err) => {
                            toast.error(
                                langResources['save.business.units']['error'],
                                {
                                    type: 'error',
                                    isLoading: false,
                                    autoClose: 5000,
                                    position: toast.POSITION.BOTTOM_RIGHT,
                                    closeOnClick: true,
                                    closeButton: true,
                                }
                            );
                        });
                });
                setIsOpenBusinessUnitsModal(false);
                window.localStorage.removeItem(LocalStorage_keys.SELECTION_RANGE);
            } else {
                const id = toast.loading(
                    langResources['save.business.units']['loading']
                );

                setIsOpenBusinessUnitsModal(false);

                let body = {
                    startDate: initDate + 'T00:00',
                    endDate: lastDate + 'T00:00',
                    businessUnitId,
                    employeeIds: getEmployeesIds(employee),
                };

                WorkshiftsSchedulingBusinessUnitAPI.saveWorkshiftBusinessUnit(body)
                    .then((res) => {
                        toast.update(id, {
                            render: langResources['save.business.units']['success'],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.business.units']['error'],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            }
        }
    };

    /**
     * Desasociamos todas las unidades de negocio por id asignado en la base de datos
     * @param businessUnitsIds number[]
     */
    const saveDisassociatedBusinessUnits = (businessUnitsIds: number[]) => {
        const id = toast.loading(
            langResources['disassociate.business.units.modal']['save.loading.label']
        );

        let body = {
            workshiftSchedulingsBusinessUnitsIds: businessUnitsIds,
        };

        WorkshiftsSchedulingBusinessUnitAPI.disassociateWorkshiftBusinessUnit(body)
            .then((res) => {
                toast.update(id, {
                    render: langResources['disassociate.business.units.modal'][
                        'save.success.label'
                    ],
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: langResources['disassociate.business.units.modal'][
                        'save.error.label'
                    ],
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    /**
     * Desasociamos todos los centros de costo por id asignado en la base de datos
     * @param costCentersIds number[]
     */
    const saveDisassociatedCostCenters = (costCentersIds: number[]) => {
        const id = toast.loading(
            langResources['disassociate.cost.center.modal']['save.loading.label']
        );

        let body = {
            workshiftSchedulingsCostCentersIds: costCentersIds,
        };

        WorkshiftsSchedulingCostCenterAPI.disassociateWorkshiftCostCenter(body)
            .then((res) => {
                toast.update(id, {
                    render: langResources['disassociate.cost.center.modal'][
                        'save.success.label'
                    ],
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: langResources['disassociate.cost.center.modal'][
                        'save.error.label'
                    ],
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    /**
     * se asigna la programación del turno seleccionado a todo el rango de empleados
     */
    const saveWorkshiftsScheduling = (id: number) => {
        let recoveredData = window.localStorage.getItem(
            LocalStorage_keys.SELECTION_RANGE
        );

        if (recoveredData) {
            let data: Array<{ employeeId: string | number; date: string }> =
                JSON.parse(recoveredData);

            toast.info(
                'Se están guardando los turnos por favor espera unos segundos...'
            );

            data.forEach((item) => {
                const body = {
                    workshiftId: id,
                    startDate: item.date,
                    endDate: item.date,
                    employeeIds: [item.employeeId],
                };

                if (body.workshiftId) {
                    WorkshiftsSchedulingAPI.createSchedulings(body)
                        .then((res) => {
                            if (res.data.length !== 0) {
                                toast.error(
                                    `${
                                        langResources['save.workshift.scheduling'][
                                            'error'
                                        ]
                                    } ${res.data.map(
                                        // @ts-ignore
                                        (day) => day?.name
                                    )}`,
                                    {
                                        type: 'error',
                                        isLoading: false,
                                        autoClose: 5000,
                                        position: toast.POSITION.BOTTOM_RIGHT,
                                        closeOnClick: true,
                                        closeButton: true,
                                    }
                                );
                            }
                            consultInfoScheduling(
                                selectedEmployees,
                                startDate,
                                endDate,
                                false,
                                []
                            );
                            setIsOpenWorkshiftModal(false);
                        })
                        .catch((err) => {
                            toast.error(
                                langResources['save.workshift.scheduling']['failed'],
                                {
                                    isLoading: false,
                                    autoClose: 5000,
                                    position: toast.POSITION.BOTTOM_RIGHT,
                                    closeOnClick: true,
                                    closeButton: true,
                                }
                            );
                        });

                    setIsOpenWorkshiftModal(false);
                }
            });

            window.localStorage.removeItem(LocalStorage_keys.SELECTION_RANGE);
        } else {
            const body = {
                workshiftId: id,
                startDate: formatDate(modalInfoWorkshift?.startDate?.toString()),
                endDate: formatDate(modalInfoWorkshift?.endDate?.toString()),
                employeeIds: getEmployeesIds(modalInfoWorkshift?.employee),
            };

            if (body.workshiftId) {
                const id = toast.loading(
                    langResources['save.workshift.scheduling']['loading']
                );

                WorkshiftsSchedulingAPI.createSchedulings(body)
                    .then((res: SchedulingWorkshiftModel) => {
                        if (res.data.length === 0) {
                            toast.update(id, {
                                render: langResources['save.workshift.scheduling'][
                                    'success'
                                ],
                                type: 'success',
                                isLoading: false,
                                autoClose: 5000,
                                position: toast.POSITION.BOTTOM_RIGHT,
                                closeOnClick: true,
                                closeButton: true,
                            });
                            setIsOpenWorkshiftModal(false);
                        } else {
                            toast.update(id, {
                                render: `${
                                    langResources['save.workshift.scheduling'][
                                        'error'
                                    ]
                                } ${res.data.map(
                                    // @ts-ignore
                                    (day) => day?.name
                                )}`,
                                type: 'error',
                                isLoading: false,
                                autoClose: 5000,
                                position: toast.POSITION.BOTTOM_RIGHT,
                                closeOnClick: true,
                                closeButton: true,
                            });
                        }
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                        setIsOpenWorkshiftModal(false);

                        if (
                            inconsistenciesTypesToFilter.length > 0 &&
                            localStorage.getItem('isValidatingInconsistencies')
                        )
                            handlerValidateNovelties(
                                selectedEmployees,
                                startDate,
                                endDate,
                                inconsistenciesTypesToFilter
                            );
                        setIsOpenDetailsInconsistenciesModal(false);
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.workshift.scheduling'][
                                'failed'
                            ],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    })
                    .finally(() => {
                        localStorage.removeItem('isValidatingInconsistencies');
                    });

                setIsOpenWorkshiftModal(false);
            }
        }
    };

    /**
     * se elimina la programacion de los turnos
     */
    const deletedWorkshiftScheduling = () => {
        const id = toast.loading(
            langResources['delete.workshift.scheduling']['loading']
        );

        let body = {
            workshiftSchedulingIds: modalInfoWorkshift.id,
        };

        WorkshiftsSchedulingAPI.deleteWorkshiftScheduling(body)
            .then((res) => {
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
                toast.update(id, {
                    render: langResources['delete.workshift.scheduling']['success'],
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            })
            .catch((err) => {
                toast.update(id, {
                    render: langResources['delete.workshift.scheduling']['error'],
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
        window.localStorage.removeItem(LocalStorage_keys.SELECTION_RANGE);
        setIsOpenDeleteWorkshiftModal(false);
    };

    /**
     * Se asignan las horas adicionales al rango seleccionado
     * @param hoursList AdditionalHourModel[]
     * @param employees EmployeeNodeModel[]
     * @param initDate string
     * @param lastDate string
     */
    const saveAdditionalHours = (
        hoursList: AdditionalHourModel[],
        employees: EmployeeNodeModel[],
        initDate: string,
        lastDate: string
    ) => {
        let recoveredDatas = window.localStorage.getItem(
            LocalStorage_keys.SELECTION_RANGE
        );

        if (recoveredDatas) {
            let data: Array<{
                employeeId: string | number;
                text: string;
                date: string;
            }> = JSON.parse(recoveredDatas);

            toast.info(
                'Se están guardando los tiempos adicionales por favor espera unos segundos...'
            );

            data.forEach((emp) => {
                hoursList.forEach((additional) => {
                    let newBody = {
                        startDate: emp.date + 'T00:00',
                        endDate: emp.date + 'T00:00',
                        observations: additional.observations,
                        employeeIds: [emp.employeeId],
                        startHour: covertFormatHour(additional.startHour),
                        endHour: covertFormatHour(additional.endHour),
                        hourTypeId: additional.hourType,
                        isOnlyOvertime: additional.isOnlyOvertime,
                    };
                    InterruptionsAPI.createAdditional(newBody)
                        .then((res) => {
                            if (res.data.length > 0) {
                                toast.warning(
                                    `${
                                        langResources['save.additional.hours'][
                                            'error'
                                        ]
                                    }: ${res.data.map(
                                        (day: any) =>
                                            day.schedulingDate.split('T')[0]
                                    )}`,
                                    {
                                        isLoading: false,
                                        closeOnClick: true,
                                        autoClose: 5000,
                                        position: toast.POSITION.BOTTOM_RIGHT,
                                        closeButton: true,
                                        className: 'w-100 h-100 ',
                                    }
                                );
                            } else {
                                consultInfoScheduling(
                                    selectedEmployees,
                                    startDate,
                                    endDate,
                                    false,
                                    []
                                );
                            }
                        })
                        .catch((err) => {
                            toast.error(
                                langResources['save.additional.hours']['failed'],
                                {
                                    type: 'error',
                                    isLoading: false,
                                    autoClose: 5000,
                                    position: toast.POSITION.BOTTOM_RIGHT,
                                    closeOnClick: true,
                                    closeButton: true,
                                }
                            );
                        });
                });
            });

            window.localStorage.removeItem(LocalStorage_keys.SELECTION_RANGE);
        } else {
            hoursList.forEach((additional) => {
                const id = toast.loading(
                    langResources['save.additional.hours']['loading']
                );

                let body = {
                    startDate: initDate + 'T00:00',
                    endDate: lastDate + 'T00:00',
                    observations: additional.observations,
                    employeeIds: getEmployeesIds(employees),
                    startHour: covertFormatHour(additional.startHour),
                    endHour: covertFormatHour(additional.endHour),
                    hourTypeId: additional.hourType ? additional.hourType : null,
                    isOnlyOvertime: additional.isOnlyOvertime,
                    interruptionReferenceId: additional.interruptionReferenceId,
                };

                InterruptionsAPI.createAdditional(body)
                    .then((res) => {
                        if (res.data.length > 0) {
                            toast.update(id, {
                                render: `${
                                    langResources['save.additional.hours']['error']
                                }: ${res.data.map(
                                    (day: any) => day.schedulingDate.split('T')[0]
                                )}`,
                                type: 'error',
                                isLoading: false,
                                closeOnClick: true,
                                autoClose: 5000,
                                position: toast.POSITION.BOTTOM_RIGHT,
                                closeButton: true,
                                className: 'w-100 h-100 ',
                            });
                        } else {
                            consultInfoScheduling(
                                selectedEmployees,
                                startDate,
                                endDate,
                                false,
                                []
                            );
                            toast.update(id, {
                                render: langResources['save.additional.hours'][
                                    'success'
                                ],
                                type: 'success',
                                isLoading: false,
                                autoClose: 5000,
                                position: toast.POSITION.BOTTOM_RIGHT,
                                closeOnClick: true,
                                closeButton: true,
                            });
                        }
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.additional.hours']['failed'],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            });
        }
    };

    /**
     * Validamos si se quiere eliminar una, todas o actualizar las horas adicionales asignadas
     * @param hoursList InterruptionModel[][]
     * @param isAllDeleted boolean
     */
    const saveUpdateAndDeleteAddititonalHours = (
        hoursList: InterruptionModel[][],
        isAllDeleted: boolean
    ) => {
        let newsAdditionalTime: NewAdditionalTimeModel[] = [];

        hoursList.forEach((add) => {
            add.forEach((additional, index) => {
                let body: NewAdditionalTimeModel = {
                    interruptionId: additional.id,
                    startHour: additional.startDate.split('T')[1],
                    endHour: additional.endDate.split('T')[1],
                    observations: additional.observations,
                    isRemove: additional.removeInterruption,
                    interruptionReferenceId: additional.interruptionReferenceId,
                };
                newsAdditionalTime.push(body);
            });
        });

        if (isAllDeleted) {
            const id = toast.loading(langResources['save.delete.hours']['loading']);
            setIsOpenUpdateAndDeleteAdditionalHoursModal(false);

            let body = {
                additionalTimeIds: newsAdditionalTime.map(
                    (add) => add.interruptionId
                ),
            };

            InterruptionsAPI.deleteAdditionalTime(body)
                .then((res) => {
                    setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                    consultInfoScheduling(
                        selectedEmployees,
                        startDate,
                        endDate,
                        false,
                        []
                    );
                    toast.update(id, {
                        render: langResources['save.delete.hours']['success'],
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                })
                .catch((err) => {
                    toast.update(id, {
                        render: langResources['save.delete.hours']['error'],
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                });
        } else {
            const additionalsToDelete = newsAdditionalTime.filter((i) => i.isRemove);

            const additionalsToUpdate = newsAdditionalTime.filter(
                (i) => !i.isRemove
            );

            let body = {
                additionalTimeIds: additionalsToDelete.map(
                    (add) => add.interruptionId
                ),
            };

            if (additionalsToDelete.length === newsAdditionalTime.length) {
                const id = toast.loading(
                    langResources['save.delete.hours']['loading']
                );

                InterruptionsAPI.deleteAdditionalTime(body)
                    .then((res) => {
                        setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                        toast.update(id, {
                            render: langResources['save.delete.hours']['success'],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.delete.hours']['error'],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            } else if (additionalsToDelete.length === 0) {
                const id = toast.loading(
                    langResources['save.update.hours']['loading']
                );

                InterruptionsAPI.updateAdditionalTime(additionalsToUpdate)
                    .then((res) => {
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                        toast.update(id, {
                            render: langResources['save.update.hours']['success'],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });

                        setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.update.hours']['error'],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                        setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                    });
            } else {
                const id = toast.loading(
                    langResources['save.update.hours']['loading']
                );

                InterruptionsAPI.deleteAdditionalTime(body).then((res) => {
                    setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                    consultInfoScheduling(
                        selectedEmployees,
                        startDate,
                        endDate,
                        false,
                        []
                    );
                    toast.update(id, {
                        render: langResources['save.delete.hours']['success'],
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                });

                InterruptionsAPI.updateAdditionalTime(additionalsToUpdate)
                    .then((res) => {
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                        toast.update(id, {
                            render: langResources['save.update.hours']['success'],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });

                        setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.update.hours']['error'],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                        setIsOpenUpdateAndDeleteAdditionalHoursModal(false);
                    });
            }
        }
    };

    /**
     * Validamos si se quiere eliminar una, todas o actualizar las interrupciones asignadas
     * @param hoursList
     * @param isAllDeleted
     */
    const saveUpdateAndDeleteInterruptions = (
        interruptionList: InterruptionModel[][],
        isAllDeleted: boolean
    ) => {
        let interruptions: UpdatedInterruption[] = [];

        interruptionList.forEach((interr) => {
            interr.forEach((interruption) => {
                let i: UpdatedInterruption = {
                    employeeId: interruption.employeeId,
                    interruptionTypeId: interruption.id,
                    isPaid: interruption.isPaid,
                    isAutomaticDiscount: interruption.isAutomaticDiscount,
                    startDate: interruption.startDate,
                    endDate: interruption.endDate,
                    observations: interruption.observations,
                    removeInterruption: interruption.removeInterruption,
                    interruptionReferenceId: interruption.interruptionReferenceId,
                };
                interruptions.push(i);
            });
        });

        if (isAllDeleted) {
            const id = toast.loading(
                langResources['save.delete.interruption']['loading']
            );

            let body = {
                interruptionIds: interruptions.map(
                    (interr) => interr.interruptionTypeId
                ),
            };

            InterruptionsAPI.deleteInterruptions(body)
                .then((res) => {
                    setIsOpenUpdateAndDeleteModal(false);
                    consultInfoScheduling(
                        selectedEmployees,
                        startDate,
                        endDate,
                        false,
                        []
                    );
                    toast.update(id, {
                        render: langResources['save.delete.interruption']['success'],
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                })
                .catch((err) => {
                    toast.update(id, {
                        render: langResources['save.delete.interruption']['error'],
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                });
        } else {
            const interruptionsToDelete = interruptions.filter(
                (i) => i.removeInterruption
            );

            const interruptionsToUpdate = interruptions.filter(
                (i) => !i.removeInterruption
            );

            let body = {
                interruptionIds: interruptionsToDelete.map(
                    (interr) => interr.interruptionTypeId
                ),
            };

            if (interruptionsToDelete.length === interruptions.length) {
                const id = toast.loading(
                    langResources['save.delete.interruption']['loading']
                );

                InterruptionsAPI.deleteInterruptions(body)
                    .then((res) => {
                        setIsOpenUpdateAndDeleteModal(false);
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                        toast.update(id, {
                            render: langResources['save.delete.interruption'][
                                'success'
                            ],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    })

                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.delete.interruption'][
                                'error'
                            ],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            } else {
                const id = toast.loading(
                    langResources['save.update.interruption']['loading']
                );

                InterruptionsAPI.deleteInterruptions(body)
                    .then((res) => {
                        setIsOpenUpdateAndDeleteModal(false);
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                    })
                    .catch((err) => console.log(err));

                InterruptionsAPI.updateInterruptions(interruptionsToUpdate)
                    .then((res) => {
                        setIsOpenUpdateAndDeleteModal(false);
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                        toast.update(id, {
                            render: langResources['save.update.interruption'][
                                'success'
                            ],
                            type: 'success',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    })
                    .catch((err) => {
                        toast.update(id, {
                            render: langResources['save.update.interruption'][
                                'error'
                            ],
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            }
        }
    };

    /**
     * Creamos las interrupciones al rango de empleados seleccionados
     * @param NewInterruptionModel
     */
    const saveInterruption = (body: NewInterruptionModel) => {
        let recoveredData = window.localStorage.getItem(
            LocalStorage_keys.SELECTION_RANGE
        );

        if (recoveredData) {
            let data: Array<{
                employeeId: string | number;
                text: string;
                date: string;
            }> = JSON.parse(recoveredData);

            toast.info(
                'Se están guardando las interrupciones por favor espera unos segundos...'
            );

            data.forEach((item) => {
                const {
                    interruptionTypeId,
                    isAdditionalHour,
                    isAutomaticDiscount,
                    isOnlyOvertime,
                    isPaid,
                    observations,
                    isWholeWorkshift,
                    workshiftsIds,
                    interruptionReferenceId,
                } = body;

                const newBody: NewInterruptionModel = {
                    employeeIds: [Number(item.employeeId)],
                    endDate:
                        item.date + 'T' + body.endDate?.toString().split('T')[1],
                    startDate:
                        item.date + 'T' + body.startDate?.toString().split('T')[1],
                    interruptionTypeId,
                    isAdditionalHour,
                    isAutomaticDiscount,
                    isOnlyOvertime,
                    isPaid,
                    observations,
                    isWholeWorkshift,
                    workshiftsIds,
                    interruptionReferenceId,
                };

                InterruptionsAPI.createInterruption(newBody)
                    .then((res) => {
                        consultInfoScheduling(
                            selectedEmployees,
                            startDate,
                            endDate,
                            false,
                            []
                        );
                    })
                    .catch((err) => {
                        toast.error(err.response.data, {
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                            position: toast.POSITION.BOTTOM_RIGHT,
                            closeOnClick: true,
                            closeButton: true,
                        });
                    });
            });

            window.localStorage.removeItem(LocalStorage_keys.SELECTION_RANGE);
        } else {
            const id = toast.loading(langResources['save.interruption']['loading']);

            InterruptionsAPI.createInterruption(body)
                .then((res) => {
                    consultInfoScheduling(
                        selectedEmployees,
                        startDate,
                        endDate,
                        false,
                        []
                    );
                    toast.update(id, {
                        render: langResources['save.interruption']['success'],
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                })
                .catch((err) => {
                    toast.update(id, {
                        render: err.response.data,
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                });
        }
    };

    /**
     * Insertamos las marcaciones
     */
    const saveAssociateMarking = (data: AssociateTimeAttendanceEvents) => {
        const id = toast.loading('Insertando Marcación...');

        TimeAttendanceEventsAPI.createTimeAttendanceEvent(data)
            .then((res) => {
                toast.update(id, {
                    render: 'Se insertó la marcación exitosamente',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                handlerValidateNovelties(
                    selectedEmployees,
                    startDate,
                    endDate,
                    inconsistenciesTypesToFilter
                );
                setIsOpenDetailsInconsistenciesModal(false);
            })
            .catch((err) => {
                toast.update(id, {
                    render: err.response.data,
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    /**
     * Insertamos las marcaciones
     */
    const saveModifyMarking = (data: UpdateTimeAttendanceEvents[]) => {
        const id = toast.loading('Actualizando Marcación...');

        TimeAttendanceEventsAPI.updateTimeAttendanceEvent(data)
            .then((res) => {
                toast.update(id, {
                    render: 'Marcación actualizada correctamente',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: 'Ocurrió un error al actualizar la marcación',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    /**
     * ASIGNAMOS DISPONIBILIDAD A EMPLEADOS
     */
    const saveAssignAvailability = (workshiftIds: number[]) => {
        const id = toast.loading('Asignando Disponibilidad...');

        AssingAvailabilityAPI.assingAvailability({
            WorkshiftSchedulingIds: workshiftIds,
        })
            .then((res) => {
                toast.update(id, {
                    render: 'Disponibilidad Asignada Exitosamente',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: 'Ocurrió un error al asignar la disponibilidad',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    /**
     * ASIGNAMOS DISPONIBILIDAD A EMPLEADOS
     */
    const removeAssignAvailability = (workshiftIds: number[]) => {
        const id = toast.loading('Eliminando Disponibilidad...');

        AssingAvailabilityAPI.removeAvailability({
            WorkshiftSchedulingIds: workshiftIds,
        })
            .then((res) => {
                toast.update(id, {
                    render: 'Disponibilidad Elimindada Exitosamente',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: 'Ocurrió un error al eliminar la disponibilidad',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                });
            });
    };

    /**
     * ASIGNAMOS DISPONIBILIDAD A EMPLEADOS
     */
    const saveWorkshiftsSundaysUsuals = (
        initDate: string,
        finalDate: string,
        employeeIds: number[]
    ) => {
        const id = toast.loading('validando dominicales habituales...');

        let body = {
            startDate: initDate,
            endDate: finalDate,
            employeeIds: employeeIds,
        };

        WorkshiftsSchedulingAPI.validateWorkshiftsSundaysUsuals(body)
            .then((res) => {
                toast.update(id, {
                    render: 'Dominicales habituales validados exitosamente',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    true,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: err.response
                        ? err.response.data
                        : 'Ocurrió un error al realizar la validación',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    const saveAssingOperationsCenter = (
        initDate: string,
        lastDate: string,
        employeesIds: (string | number)[],
        operationCenterId: number
    ) => {
        const id = toast.loading('Asignando Centros de operaciones...');

        OperationsCenterRepository.create({
            EmployeesIds: employeesIds,
            EndDate: lastDate,
            OperationCenterId: operationCenterId,
            StartDate: initDate,
        })
            .then((res) => {
                toast.update(id, {
                    render: 'Centros de operaciones asignados',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: 'Ocurrió un error asignar los centros de operaciones',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    const saveADeleteOperationsCenter = (ids: number[]) => {
        const id = toast.loading('Eliminando Centros de operaciones...');

        OperationsCenterRepository.delete({
            WorkshiftSchedulingOperationCentersIds: ids,
        })
            .then((res) => {
                toast.update(id, {
                    render: 'Centros de operaciones eliminados',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
                consultInfoScheduling(
                    selectedEmployees,
                    startDate,
                    endDate,
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: 'Ocurrió un error eliminar los centros de operaciones',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    const saveBonusAssignments = (
        bonusRuleIds: number[],
        initDate: string | number | undefined,
        lastDate: string | number | undefined,
        employees: EmployeeNodeModel[]
    ) => {
        const id = toast.loading(langResources['loader.saving.bonus']);

        PreliquidationDaySummariesBonusRulesAPI.create({
            bonusRuleIds,
            startDate: initDate?.toString() || '',
            endDate: lastDate?.toString() || '',
            employeeIds: getEmployeesIds(employees),
        })
            .then((res) => {
                if (res.message) {
                    setMessage(res.message);
                    setShowMessageModal(true);
                    toast.dismiss(id);
                } else {
                    toast.update(id, {
                        render: langResources['loader.save.bonus.success'],
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                }
                consultInfoScheduling(
                    selectedEmployees,
                    startDate?.toString() || '',
                    endDate?.toString() || '',
                    false,
                    []
                );
            })
            .catch((err) => {
                toast.update(id, {
                    render: err?.message || langResources['loader.save.bonus.failed'],
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    const saveMultipleCostCentersByHourRanges = (data: {
        startDate: string;
        endDate: string;
        employees: Array<{
            employeeId: number;
            isBulkAssignment: boolean;
            BulkAssignmentStartTime ?: string | null;
            BulkAssignmentEndTime ?: string | null;
            costCenters: Array<{
                costCenterId: number;
                costCenterName: string;
                date: string;
                startTime: string;
                endTime: string;
                schedulingCostCenterId: number;
                workshiftSchedulingId: number;
            }>;
        }>;
        costCenterIdsToRemove: number[];
    }) => {
        const id = toast.loading('Asignando múltiples centros de costo...');

        const formatTimeToTimeSpan = (time: string): string => {

            if (time.split(':').length === 2) {
                return `${time}:00`;
            }
            return time;
        };

        const body = {
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
            employees: data.employees.map((employee) => ({
                employeeId: employee.employeeId,
                isBulkAssignment: employee.isBulkAssignment,
                BulkAssignmentStartTime: employee.BulkAssignmentStartTime,
                BulkAssignmentEndTime: employee.BulkAssignmentEndTime,
                costCenters: employee.costCenters.map((cc) => ({
                    schedulingCostCenterId: cc.schedulingCostCenterId,
                    costCenterId: cc.costCenterId,
                    costCenterName: cc.costCenterName,
                    date: new Date(cc.date),
                    startTime: formatTimeToTimeSpan(cc.startTime),
                    endTime: formatTimeToTimeSpan(cc.endTime),
                    workshiftSchedulingId: cc.workshiftSchedulingId,
                })),
            })),
            costCenterIdsToRemove: data.costCenterIdsToRemove || [],
        };

        WorkshiftsSchedulingCostCenterAPI.associateByHourRanges(body)
            .then((res) => {
                toast.dismiss(id);

                setCostCenterScheduleResults({
                    acceptedCostCenterSchedules: res.accepted || [],
                    rejectionOfCostCenterSchedules: res.rejected || [],
                });
                setIsOpenCostCenterScheduleResultsModal(true);

                setTimeout(() => {
                    consultInfoScheduling(
                        selectedEmployees,
                        startDate?.toString() || '',
                        endDate?.toString() || '',
                        false,
                        []
                    );
                }, 1000);
            })
            .catch((err) => {
                toast.update(id, {
                    render: 'Ocurrió un error al asignar los centros de costo',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            });
    };

    return (
        <div
            className={`table__wrapper bg-danger ${
                isCollapseTree && 'table__wrapper--collapse'
            } p-0`}
        >
            {
                showMessageModal && (
                    <MessageModal
                        message={message}
                        onClose={() => setShowMessageModal(false)}
                    />
                )
            }
            <ContextMenu
                targetId='myContextMenu'
                saveAssignAvailability={saveAssignAvailability}
                removeAssignAvailability={removeAssignAvailability}
                startDate={startDate}
                endDate={endDate}
                treeEmployees={selectedEmployees}
                openWorkshiftModal={handlerOpenWorkshiftModal}
                openBonusModal={handlerOpenBonusModal}
                openDeleteWorkshiftModal={handlerOpenDeleteWorkshiftModal}
                openUpdateAndDeleteInterruptionsModal={
                    handlerOpenUpdateAndDeleteInterruptionsModal
                }
                openUpdateAndDeleteAdditionalHoursModal={
                    handlerOpenUpdateAndDeleteAdditionalHoursModal
                }
                openInterruptionModal={handlerOpenInteruptionModal}
                openAdditionalHours={handlerOpenAdditionalHours}
                openCostCenterModal={handlerOpenCostCenterModal}
                openMultipleCostCentersByHourRangesModal={handlerOpenMultipleCostCentersByHourRangesModal}
                saveExceptionalWorkshiftsScheduling={
                    saveExceptionalWorkshiftsScheduling
                }
                openBusinessUnitsModal={handlerBusinessUnitsModal}
                openAidTypesModal={handlerOpenAidTypeModal}
                openDisassociatedBusinessUnitsModal={
                    handlerDisassociatedBusinessUnitsModal
                }
                openDisassociatedCostCenterModal={
                    handlerOpenDisassociatedCostCenterModal
                }
                openCopyWorkshiftsSchedulings={handlerCopyWorkshiftsSchedulings}
                openSavePasteWorkshiftsSchedulings={
                    handlerSavePasteWorkshiftsSchedulings
                }
                modifyAssociateTimeAttendanceEvents={handlerOpenModifyMarkingModal}
                openAssociateTimeAttendanceEvents={handlerOpenAssociateMarkingModal}
                handlerValidateNovelties={handlerValidateNovelties}
            />

            <ModalsWrapper
                deletedWorkshiftScheduling={deletedWorkshiftScheduling}
                saveCostCenter={saveCostCenter}
                saveDisassociatedCostCenters={saveDisassociatedCostCenters}
                saveBusinessUnits={saveBusinessUnits}
                saveDisassociatedBusinessUnits={saveDisassociatedBusinessUnits}
                saveWorkshiftsScheduling={saveWorkshiftsScheduling}
                saveInterruption={saveInterruption}
                saveUpdateAndDeleteAddititonalHours={
                    saveUpdateAndDeleteAddititonalHours
                }
                saveMultipleCostCentersByHourRanges={saveMultipleCostCentersByHourRanges}
                saveUpdateAndDeleteInterruptions={saveUpdateAndDeleteInterruptions}
                saveAdditionalHours={saveAdditionalHours}
                saveAssociateMarking={saveAssociateMarking}
                saveModifyMarking={saveModifyMarking}
                selectedEmployees={selectedEmployees}
                saveAssingOperationsCenter={saveAssingOperationsCenter}
                saveADeleteOperationsCenter={saveADeleteOperationsCenter}
                saveWorkshiftsSundaysUsuals={saveWorkshiftsSundaysUsuals}
                saveBonusAssignments={saveBonusAssignments}
            />

            <Table
                startDate={startDate}
                endDate={endDate}
                treeEmployees={selectedEmployees}
                treeEmployeesCopy={selectedEmployeesCopy}
                groupsList={groupList}
                grid={grid}
                setGrid={setGrid}
            />
        </div>
    );
};
